import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { setSearchText, setDateRange, setSortOrder, setSelectedTaskTypes, setSortCriteria } from './TasksSlice';
import DateRangePicker from './DateRangePicker';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';
import styles from '../../components/FilterManagementView.module.css';
import { openModal } from '../../components/PerinHealthModelSlice';
import inputStyles from '../../views/ManagementPages.module.css';
import { SortingDownArrowIcon, SortingUpArrowIcon } from '../../components/icons/Icons';

const TaskFilterView = (props) => {
    const dispatch = useDispatch();
    const {
        taskOptions,
        selectedTaskTypes = [],
        selectedRows,
        searchText,
        filter, 
        sortCriteria,
        sortOrder
    } = useSelector((state) => state.taskListPanel);
    const addDisabled = selectedRows.length <= 0;;

    const handleSearchChange = (e) => {
        dispatch(setSearchText(e.target.value));
    };

    const handleDateRangeSave = (startDate, endDate, normalizedStartDate, normalizedEndDate) => {
        if (startDate && endDate) {
            dispatch(setDateRange({ key: 'startDate', value: new Date(normalizedStartDate).toLocaleDateString() }));
            dispatch(setDateRange({ key: 'endDate', value: new Date(normalizedEndDate).toLocaleDateString() }));
            dispatch(setDateRange({ key: 'startDateTimestamp', value: normalizedStartDate }));
            dispatch(setDateRange({ key: 'endDateTimestamp', value: normalizedEndDate }));
        } else if (startDate) {
            dispatch(setDateRange({ key: 'startDate', value: new Date(normalizedStartDate).toLocaleDateString() }));
            dispatch(setDateRange({ key: 'startDateTimestamp', value: normalizedStartDate }));
        } else if (endDate) {
            dispatch(setDateRange({ key: 'endDate', value: new Date(normalizedEndDate).toLocaleDateString() }));
            dispatch(setDateRange({ key: 'endDateTimestamp', value: normalizedEndDate }));
        }
    };

    const handleClearDateRange = () => {
        dispatch(setDateRange({ key: 'startDate', value: '' }));
        dispatch(setDateRange({ key: 'endDate', value: '' }));
        dispatch(setDateRange({ key: 'startDateTimestamp', value: '' }));
        dispatch(setDateRange({ key: 'endDateTimestamp', value: '' }));
    };

    const safeSelectedTaskTypes = Array.isArray(selectedTaskTypes) ? selectedTaskTypes : [];

    const checkSelectedItem = (value) => {
        return safeSelectedTaskTypes.some(item => item.value === value);
    };

    const handleTaskChange = (item) => {
        if (checkSelectedItem(item.value)) {
            // Remove item from selectedTaskTypes
            const updatedSelectedTaskTypes = safeSelectedTaskTypes.filter(selectedItem => selectedItem?.type?.toLowerCase().includes(searchText?.toLowerCase(item.value)));
            dispatch(setSelectedTaskTypes(updatedSelectedTaskTypes));
        } else {
            // Add item to selectedTaskTypes
            const updatedSelectedTaskTypes = [...safeSelectedTaskTypes, item];
            dispatch(setSelectedTaskTypes(updatedSelectedTaskTypes));
        }
    };

    const onClickHandler = (event) => {
        if (event.target.textContent === 'Mark as Complete') {
            dispatch(
                openModal({
                    activeModalId: "TaskListPanel",
                    header: "Mark as Complete?",
                    message: "Marked tasks will be considered finished and will move to the Completed Tasks tab.",
                    buttons: [
                        { label: 'No', variant: 'secondary', onClickKey: 'NO' },
                        { label: 'Yes', variant: 'primary', onClickKey: 'YES_MARK_COMPLETE' },
                    ],
                })
            );
        } else if (event.target.textContent === 'Archive') {
            dispatch(
                openModal({
                    activeModalId: "TaskListPanel",
                    header: "Archive Task?",
                    message: "Archived tasks will no longer be available.",
                    buttons: [
                        { label: 'No', variant: 'secondary', onClickKey: 'NO' },
                        { label: 'Yes', variant: 'primary', onClickKey: 'YES_ARCHIVE' },
                    ],
                })
            );
        }
    };

    const handleSortChange = (e) => {
        const value = e.target.value;
        if (value==="name") {
            dispatch(setSortOrder("asc"));
        } else {
            dispatch(setSortOrder("desc"));
        }
        dispatch(setSortCriteria(value));
    };

    const handleSortOrderChange = (direction) => {
        dispatch(setSortOrder(direction));
    };

    return (
        <div className={styles.TaskSection}>
            <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
                <div style={{ position: "absolute", paddingLeft: "10px" }}>
                    <FontAwesomeIcon icon="fa-solid fa-magnifying-glass" size="xs" style={{ color: "D9D9D9" }} />
                </div>
                <input
                    type="text"
                    placeholder="Search..."
                    value={searchText}
                    onChange={handleSearchChange}
                    className={styles.SearchInput}
                />
            </div>

            <div style={{ position: "relative" }}>
                <DateRangePicker
                    onSave={handleDateRangeSave}
                    onClear={handleClearDateRange}
                />
            </div>
            <div style={{ position: "relative" }}>
                <span className="floating-label">Sort</span>
                <select
                    className={inputStyles["styled-input-select"]}
                    value={sortCriteria}
                    onChange={handleSortChange}
                >
                    <option value="default">Default</option>
                    <option value="name">Patient</option>
                    <option value="created_date">Created Date</option>
                    {filter?.value === 'Completed' &&
                        <option value="completed_date">Completed Date</option>
                    }
                </select>
            </div>
            <div className={'sorting-icon'}>
                <div onClick={() => handleSortOrderChange("asc")}>
                    <SortingUpArrowIcon color={(sortOrder === "asc") ? "#686869" : "#B9B9B9"}  size="12"/>
                </div>
                <div onClick={() => handleSortOrderChange("desc")}>
                    <SortingDownArrowIcon color={(sortOrder === "desc") ? "#686869" : "#B9B9B9"}  size="12"/>
                </div>
            </div>
            <div style={{ position: "relative" }}>
                <MultiSelectDropdown inputWidth='25vh'
                    options={taskOptions?.filter((task) => task?.status === filter?.value)}
                    label='label'
                    checkedKey='value'
                    checkSelectedItem={checkSelectedItem}
                    handleChange={handleTaskChange}
                    selectedItems={safeSelectedTaskTypes}
                    name='Task Type'
                />
            </div>

            <Button
                variant="secondary"
                className={addDisabled ? styles.AddContentButtonDisabled : styles.AddContentButton}
                disabled={addDisabled}
                onClick={(event) => onClickHandler(event)}
            >
                {filter?.value === 'Pending' ? 'Mark as Complete' : 'Archive'}
            </Button>

        </div>
    );
};

export default TaskFilterView;
